import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  type User
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase/config';

// Create Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
  // Reduce cookie usage by limiting scopes
  include_granted_scopes: 'false'
});

// Add additional auth settings to minimize cookies
googleProvider.addScope('email');
googleProvider.addScope('profile');

export const loginWithGoogle = async () => {
  // If Firebase is not configured, return mock user (demo mode)
  if (!isFirebaseConfigured || !auth) {
    console.log('🔧 Demo mode: Mock Google login');
    const mockUser = {
      uid: 'demo_user_123',
      email: 'demo@hearth.app',
      displayName: 'Demo User',
    } as User;
    return { user: mockUser, error: null };
  }

  try {
    const result = await signInWithPopup(auth, googleProvider);
    return { user: result.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

export const loginUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

export const registerUser = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error: any) {
    return { user: null, error: error.message };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error: any) {
    return { error: error.message };
  }
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  // If Firebase is not configured, simulate auth state (demo mode)
  if (!isFirebaseConfigured || !auth) {
    // In demo mode, simulate being logged out initially
    setTimeout(() => callback(null), 100);
    return () => {}; // Return empty unsubscribe function
  }

  return onAuthStateChanged(auth, callback);
};